'use client'
import { useEffect, useRef, useState } from 'react'
import { useFormik, FormikProvider } from 'formik'
import { useTranslations } from 'next-intl'
import { Loader2 } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { FormInput, FormSelect } from '@/components/form-inputs'
import { KNOWLEDGE_SOURCE_PROVIDERS } from '@/lib/knowledge-sources/providers'
import { useKnowledgeSourcesQuery } from '@/hooks/queries/knowledge-sources'
import { useKnowledgeSourceMutation } from '@/hooks/mutations/knowledge-sources'
import type { KnowledgeAgentRef, KnowledgeSourceProvider } from '@/lib/knowledge-sources/types'
import { knowledgeSourceProviderFlows, type KnowledgeSourceChooseFiles } from './provider-flows'
import { KnowledgeSourceProviderIcon } from './provider-icon'
import { KnowledgeSourceSelectedFiles } from './selected-files'

export function KnowledgeSourceConnectDialog({ open, onOpenChange, connectionId, folderId, agent, returnTo = 'knowledge-base', presentation = 'dialog', isDefault, onImported }: {
  presentation?: 'dialog' | 'inline'; isDefault?: boolean; onImported?: (result: { folderId: string }) => void | Promise<void>;
  returnTo?: 'integrations' | 'knowledge-base'; open: boolean; onOpenChange(open: boolean): void; connectionId?: string; folderId?: string; agent?: KnowledgeAgentRef;
}) {
  const t = useTranslations('KnowledgeSources')
  const query = useKnowledgeSourcesQuery()
  const mutation = useKnowledgeSourceMutation()
  const nameEdited = useRef(false)
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState(false)
  const [picking, setPicking] = useState(false)
  const [chooseFiles, setChooseFiles] = useState<KnowledgeSourceChooseFiles>()
  const [pickerReady, setPickerReady] = useState(false)
  const [pickerLoading, setPickerLoading] = useState(Boolean(connectionId))
  const [files, setFiles] = useState<Array<{ id: string; name: string }>>([])
  const form = useFormik({ initialValues: { connectionId: connectionId ?? '', name: KNOWLEDGE_SOURCE_PROVIDERS[0].name, provider: KNOWLEDGE_SOURCE_PROVIDERS[0].id as KnowledgeSourceProvider, selectionId: '', fileIds: [] as string[] }, enableReinitialize: true,
    onSubmit: (values, { setErrors }) => mutation.mutateAsync({ kind: 'select', values: { ...values, folderId, agent, ...(isDefault ? { isDefault: true } : {}) },
      setErrors }).then(async result => {
        if (result && typeof result === 'object' && 'folderId' in result && typeof result.folderId === 'string') await onImported?.({ folderId: result.folderId })
        onOpenChange(false); setFiles([])
      }).catch(() => {}) })
  const setFieldValue = form.setFieldValue
  const provider = query.data?.connections.find(row => row.id === form.values.connectionId)?.provider ?? form.values.provider
  const connectedAccounts = (query.data?.connections ?? []).filter(row => row.status === 'connected' && row.provider === form.values.provider)
  const flow = knowledgeSourceProviderFlows[provider]
  const defaultName = KNOWLEDGE_SOURCE_PROVIDERS.find(item => item.id === provider)?.name ?? KNOWLEDGE_SOURCE_PROVIDERS[0].name
  useEffect(() => {
    if (!open) { setFiles([]); setError(undefined); setPickerLoading(false); return }
    let mounted = true
    setPickerReady(false)
    setChooseFiles(undefined)
    setError(undefined)
    if (!form.values.connectionId) { setPickerLoading(false); return }
    setPickerLoading(true)
    void (async () => {
      try {
        const prepared = await flow.prepareSelection(form.values.connectionId)
        if (mounted) {
          setChooseFiles(() => prepared.chooseFiles)
          setPickerReady(true)
          if (!nameEdited.current) void setFieldValue('name', prepared.suggestedName ?? defaultName)
        }
      } catch { if (mounted) setError(t('pickerFailed')) }
      finally { if (mounted) setPickerLoading(false) }
    })()
    return () => { mounted = false }
  }, [open, t, form.values.connectionId, flow, setFieldValue, defaultName])
  useEffect(() => {
    setFiles([])
    void setFieldValue('fileIds', [])
    void setFieldValue('selectionId', '')
    // The selection belongs to this account; changing it requires a new Picker confirmation.
  }, [form.values.connectionId, setFieldValue])
  async function connect() {
    setBusy(true); setError(undefined)
    try {
      const result = await flow.connect({ returnTo, agent, ...(presentation === 'inline' ? { popup: true } : {}) })
      if (result?.connectionId) await form.setFieldValue('connectionId', result.connectionId)
      await query.refetch()
    } catch { setError(t('connectionFailed')) } finally { setBusy(false) }
  }
  async function select() {
    if (!chooseFiles) return
    setBusy(true); setPicking(true); setError(undefined)
    try {
      // Invoke the prepared provider flow synchronously to retain popup user activation.
      const selected = await chooseFiles()
      if (selected.files.length && selected.selectionId) {
        setFiles(selected.files)
        await form.setFieldValue('fileIds', selected.files.map(file => file.id))
        await form.setFieldValue('selectionId', selected.selectionId)
      }
    } catch { setError(t('pickerFailed')) } finally { setBusy(false); setPicking(false) }
  }
  const content = <FormikProvider value={form}><form onSubmit={form.handleSubmit} className="space-y-4">
        {!connectionId && <FormSelect name="provider" label={t('provider')} options={KNOWLEDGE_SOURCE_PROVIDERS.map(provider => ({ value: provider.id, label: provider.name, icon: <KnowledgeSourceProviderIcon provider={provider.id} /> }))} />}
        {!connectionId && <div className="space-y-2">
          {connectedAccounts.length > 0 && <FormSelect name="connectionId" label={t('account')} placeholder={t('chooseAccount')}
            options={connectedAccounts.map(row => ({ value: row.id, label: row.accountLabel }))} />}
          <Button type="button" variant="outline" onClick={() => void connect()} disabled={busy} aria-busy={busy && !picking}>
            {busy && !picking && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}<span>{t('connectAccount')}</span>
          </Button>
        </div>}
        {!folderId && <FormInput name="name" label={t('sourceName')} onChange={event => { nameEdited.current = true; form.handleChange(event) }} />}
        {connectionId && <p className="break-all text-sm"><span>{query.data?.connections.find(row => row.id === connectionId)?.accountLabel}</span></p>}
        {form.errors.connectionId && <p role="alert" className="text-sm text-destructive"><span key={form.errors.connectionId}>{form.errors.connectionId}</span></p>}
        {agent && <p className="text-sm"><span>{t('assignmentNotice')}</span></p>}
        <p className="text-sm text-muted-foreground"><span>{t('newFiles')}</span></p>
        <Button type="button" variant="outline" onClick={() => void select()} disabled={!pickerReady || !form.values.connectionId || busy} aria-busy={pickerLoading || picking}>
          {(pickerLoading || picking) && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}<span>{pickerLoading ? t('loading') : t('selectFiles')}</span>
        </Button>
        <KnowledgeSourceSelectedFiles key={form.values.selectionId} files={files.filter(file => form.values.fileIds.includes(file.id))}
          onRemove={id => void form.setFieldValue('fileIds', form.values.fileIds.filter(selectedId => selectedId !== id))} />
        {typeof form.errors.fileIds === 'string' && <p role="alert" className="text-sm text-destructive"><span key={form.errors.fileIds}>{form.errors.fileIds}</span></p>}
        {error && <p role="alert" className="text-sm text-destructive"><span key={error}>{error}</span></p>}
        <div className="flex flex-wrap justify-end gap-2">{presentation === 'dialog' && <Button type="button" variant="outline" onClick={() => onOpenChange(false)}><span>{t('cancel')}</span></Button>}
          <Button type="submit" disabled={!form.values.fileIds.length || !form.values.selectionId || !form.values.connectionId || busy || form.isSubmitting} aria-busy={form.isSubmitting}>
            {form.isSubmitting && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}<span key={folderId}>{folderId ? t('addFiles') : t('startSync')}</span>
          </Button></div>
      </form></FormikProvider>
  if (presentation === 'inline') return <div className="space-y-4 p-4 sm:p-6">{content}</div>
  return <Dialog open={open} modal={!picking} onOpenChange={next => { if (!picking) onOpenChange(next) }}>
    <DialogContent className="max-h-[85dvh] overflow-y-auto sm:max-w-xl"
      onInteractOutside={event => { if (picking) event.preventDefault() }}
      onEscapeKeyDown={event => { if (picking) event.preventDefault() }}>
      <DialogHeader><DialogTitle><span>{t('connect')}</span></DialogTitle><DialogDescription><span>{t('guidance')}</span></DialogDescription></DialogHeader>
      {content}
    </DialogContent>
  </Dialog>
}
